import React, { useEffect, useState } from 'react';
import { fetchAds } from '../services/api';

interface Props {
  format: 'sidebar' | 'billboard' | 'generic' | 'ppcsidebar' | 'ppcbillboard';
  count?: number;
  stateSlug: string;
  city?: string;
  className?: string;
  label?: string;
}

/**
 * Validates that ad markup from the adserver contains only the expected safe
 * patterns. The adserver generates markup from controlled HTML templates in
 * the /markup directory. This function uses DOMParser to inspect the actual
 * parsed DOM for dangerous elements and attributes, which is more reliable
 * than simple substring/regex checks against raw HTML strings.
 */
const isSafeAdMarkup = (markup: string): boolean => {
  if (!markup || typeof markup !== 'string') return false;

  // Use the browser's HTML parser for accurate DOM inspection
  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(markup, 'text/html');
  } catch {
    return false;
  }

  // Reject if any <script> elements are present
  if (doc.querySelectorAll('script').length > 0) return false;

  // Reject if any element has an event-handler attribute (onclick, onerror, etc.)
  const allElements = doc.body.querySelectorAll('*');
  for (const el of Array.from(allElements)) {
    for (const attr of Array.from(el.attributes)) {
      if (/^on\w+$/i.test(attr.name)) return false;
      // Reject javascript: in href/src/action attributes
      if (/^(href|src|action|formaction|data)$/i.test(attr.name) && /^\s*javascript:/i.test(attr.value)) return false;
    }
  }

  // Markup from our adserver should contain a known ad class or our tracking path
  const lower = markup.toLowerCase();
  const hasKnownAdClass =
    lower.includes('asc-billboard') ||
    lower.includes('asc-sidebar') ||
    lower.includes('asc-generic') ||
    lower.includes('asc-listing') ||
    lower.includes('fp/n/'); // click/impression tracking path
  return hasKnownAdClass;
};

const AdUnit: React.FC<Props> = ({ format, count = 1, stateSlug, city, className, label }) => {
  const [adMarkup, setAdMarkup] = useState<string[]>([]);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const siteBase = 'https://topratedrehabs.com';
        const cityPath = city ? `/${encodeURIComponent(city.toLowerCase())}` : '';
        const referer = `${siteBase}/rehabs/${stateSlug}${cityPath}/`;
        const data = await fetchAds(referer, { [format]: count });

        if (data.status === 'OK' && data.results?.markup_ads) {
          const safeMarkups = data.results.markup_ads
            .map(a => a.body)
            .filter(Boolean)
            .filter(isSafeAdMarkup);
          setAdMarkup(safeMarkups);
        } else {
          setError(data.status || 'No ads available');
        }
      } catch (err: any) {
        setError('Unable to load ads');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [format, count, stateSlug, city]);

  // Return null during loading and when empty so the parent grid cell is
  // not created at all — prevents blank grid holes from empty ad slots.
  if (loading || error || adMarkup.length === 0) return null;

  return (
    <div className={className}>
      {label && (
        <div style={{ fontSize: '11px', color: '#bbb', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {label}
        </div>
      )}
      {adMarkup.map((markup, i) => (
        // Markup is validated by isSafeAdMarkup before rendering.
        // All content originates from our own adserver's controlled HTML templates.
        <div key={i} dangerouslySetInnerHTML={{ __html: markup }} /> // eslint-disable-line react/no-danger
      ))}
    </div>
  );
};

export default AdUnit;
